import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Plus,
  Edit2,
  UserX,
  Shield,
  Eye,
  EyeOff,
  X,
  Check,
  AlertCircle,
  Trash2,
  Settings,
} from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
});

const ALL_PERMISSIONS = [
  { key: 'view_dashboard', label: 'View Dashboard', group: 'General' },
  { key: 'view_orders', label: 'View Orders', group: 'Orders' },
  { key: 'update_order_status', label: 'Update Order Status', group: 'Orders' },
  { key: 'verify_payments', label: 'Verify Payments', group: 'Orders' },
  { key: 'reject_payments', label: 'Reject Payments', group: 'Orders' },
  { key: 'add_timeline', label: 'Add Timeline Entries', group: 'Orders' },
  { key: 'view_timeline', label: 'View Timeline', group: 'Orders' },
  { key: 'view_products', label: 'View Products', group: 'Products' },
  { key: 'manage_products', label: 'Manage Products', group: 'Products' },
  { key: 'manage_stock', label: 'Manage Stock', group: 'Products' },
  { key: 'view_gold_rates', label: 'View Gold Rates', group: 'Products' },
  { key: 'update_gold_rates', label: 'Update Gold Rates', group: 'Products' },
  { key: 'view_members', label: 'View Members', group: 'Members' },
  { key: 'manage_members', label: 'Manage Members', group: 'Members' },
  { key: 'view_reports', label: 'View Reports', group: 'Reports' },
  { key: 'manage_bank_details', label: 'Manage Bank Details', group: 'Settings' },
  { key: 'manage_settings', label: 'Manage Site Settings', group: 'Settings' },
  { key: 'manage_staff', label: 'Manage Staff', group: 'Settings' },
];

const permissionGroups = [...new Set(ALL_PERMISSIONS.map((p) => p.group))];

const COLOR_OPTIONS = [
  { value: 'bg-red-100 text-red-700 border-red-200', label: 'Red' },
  { value: 'bg-green-100 text-green-700 border-green-200', label: 'Green' },
  { value: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Blue' },
  { value: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Amber' },
  { value: 'bg-purple-100 text-purple-700 border-purple-200', label: 'Purple' },
  { value: 'bg-pink-100 text-pink-700 border-pink-200', label: 'Pink' },
  { value: 'bg-stone-100 text-stone-700 border-stone-200', label: 'Gray' },
];

export default function AdminStaff({ onNavigate, user }) {
  const [tab, setTab] = useState('staff'); // 'staff' | 'roles'
  const [staffList, setStaffList] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Staff form
  const [showStaffForm, setShowStaffForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [savingStaff, setSavingStaff] = useState(false);
  const [staffForm, setStaffForm] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: '',
    permissions: [],
  });

  // Role form
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [savingRole, setSavingRole] = useState(false);
  const [roleForm, setRoleForm] = useState({
    name: '',
    label: '',
    color: COLOR_OPTIONS[6].value,
    permissions: [],
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sr, rr] = await Promise.all([
        fetch(`${API_BASE_URL}/staff`, { headers: authHeaders() }),
        fetch(`${API_BASE_URL}/staff/roles`, { headers: authHeaders() }),
      ]);
      const [sd, rd] = await Promise.all([sr.json(), rr.json()]);
      if (sd.success) setStaffList(sd.staff);
      if (rd.success) setRoles(rd.roles);
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const showMsg = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  };

  // ── Staff handlers ──────────────────────────────────────────
  const openAddStaff = () => {
    const defaultRole = roles.find((r) => r.name === 'staff') || roles[0];
    setEditingStaff(null);
    setStaffForm({
      username: '',
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      phone: '',
      role: defaultRole?.name || '',
      permissions: defaultRole?.permissions || [],
    });
    setShowStaffForm(true);
  };

  const openEditStaff = (s) => {
    const roleObj = roles.find((r) => r.name === s.role);
    const perms =
      s.permissions && s.permissions.length > 0 ? s.permissions : roleObj?.permissions || [];
    setEditingStaff(s);
    setStaffForm({
      username: s.username,
      email: s.email,
      password: '',
      first_name: s.first_name || '',
      last_name: s.last_name || '',
      phone: s.phone || '',
      role: s.role,
      permissions: perms,
    });
    setShowStaffForm(true);
  };

  const handleStaffRoleChange = (roleName) => {
    const roleObj = roles.find((r) => r.name === roleName);
    setStaffForm((prev) => ({ ...prev, role: roleName, permissions: roleObj?.permissions || [] }));
  };

  const toggleStaffPerm = (key) => {
    setStaffForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter((p) => p !== key)
        : [...prev.permissions, key],
    }));
  };

  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    setSavingStaff(true);
    setError(null);
    try {
      const url = editingStaff
        ? `${API_BASE_URL}/staff/${editingStaff.id}`
        : `${API_BASE_URL}/staff`;
      const method = editingStaff ? 'PUT' : 'POST';
      const body = editingStaff
        ? {
            role: staffForm.role,
            first_name: staffForm.first_name,
            last_name: staffForm.last_name,
            phone: staffForm.phone,
            permissions: staffForm.permissions,
          }
        : staffForm;
      const r = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(body) });
      const d = await r.json();
      if (!d.success) {
        setError(d.error);
        return;
      }
      showMsg(editingStaff ? 'Staff updated' : 'Staff account created');
      setShowStaffForm(false);
      setEditingStaff(null);
      fetchAll();
    } catch {
      setError('Connection error');
    } finally {
      setSavingStaff(false);
    }
  };

  const handleDeactivate = async (id, name) => {
    if (!window.confirm(`Deactivate ${name}'s account?`)) return;
    try {
      const r = await fetch(`${API_BASE_URL}/staff/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const d = await r.json();
      if (d.success) {
        showMsg('Account deactivated');
        fetchAll();
      } else setError(d.error);
    } catch {
      setError('Connection error');
    }
  };

  // ── Role handlers ───────────────────────────────────────────
  const openAddRole = () => {
    setEditingRole(null);
    setRoleForm({ name: '', label: '', color: COLOR_OPTIONS[6].value, permissions: [] });
    setShowRoleForm(true);
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    setRoleForm({
      name: role.name,
      label: role.label,
      color: role.color,
      permissions: role.permissions || [],
    });
    setShowRoleForm(true);
  };

  const toggleRolePerm = (key) => {
    setRoleForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter((p) => p !== key)
        : [...prev.permissions, key],
    }));
  };

  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    setSavingRole(true);
    setError(null);
    try {
      const url = editingRole
        ? `${API_BASE_URL}/staff/roles/${editingRole.id}`
        : `${API_BASE_URL}/staff/roles`;
      const method = editingRole ? 'PUT' : 'POST';
      const r = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify(roleForm),
      });
      const d = await r.json();
      if (!d.success) {
        setError(d.error);
        return;
      }
      showMsg(editingRole ? 'Role updated' : 'Role created');
      setShowRoleForm(false);
      setEditingRole(null);
      fetchAll();
    } catch {
      setError('Connection error');
    } finally {
      setSavingRole(false);
    }
  };

  const handleDeleteRole = async (role) => {
    if (!window.confirm(`Delete role "${role.label}"?`)) return;
    try {
      const r = await fetch(`${API_BASE_URL}/staff/roles/${role.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const d = await r.json();
      if (d.success) {
        showMsg('Role deleted');
        fetchAll();
      } else setError(d.error);
    } catch {
      setError('Connection error');
    }
  };

  const getRoleObj = (name) => roles.find((r) => r.name === name);

  return (
    <div className="pt-44 pb-12 min-h-screen bg-stone-50">
      <div className="max-w-5xl mx-auto px-6">
        {(user?.role === 'admin' || (user?.permissions || []).includes('view_dashboard')) && (
          <button
            onClick={() => onNavigate('admin-dashboard')}
            className="flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-6 transition-colors"
          >
            <ArrowLeft size={14} /> Back to Dashboard
          </button>
        )}

        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="font-serif text-3xl text-stone-900 flex items-center gap-3">
              Staff Management <Shield size={24} className="text-gold-600" />
            </h1>
            <p className="text-stone-500 text-sm mt-1">Manage staff accounts and custom roles</p>
          </div>
          <button
            onClick={tab === 'staff' ? openAddStaff : openAddRole}
            className="flex items-center gap-2 bg-stone-900 text-white px-5 py-3 text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors"
          >
            <Plus size={14} /> {tab === 'staff' ? 'Add Staff' : 'Add Role'}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-stone-200 mb-6">
          {[
            ['staff', 'Staff Accounts'],
            ['roles', 'Roles & Permissions'],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-6 py-3 text-xs uppercase tracking-widest font-medium transition-colors ${tab === key ? 'border-b-2 border-stone-900 text-stone-900' : 'text-stone-500 hover:text-stone-700'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm flex items-center gap-2">
            <AlertCircle size={16} />
            {error}
            <button onClick={() => setError(null)} className="ml-auto">
              <X size={14} />
            </button>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 text-sm rounded-sm flex items-center gap-2">
            <Check size={16} />
            {success}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-gold-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : tab === 'staff' ? (
          /* ── Staff Table ── */
          <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden">
            {staffList.length === 0 ? (
              <div className="text-center py-16 text-stone-400">
                <Shield size={40} className="mx-auto mb-3 opacity-30" />
                <p>No staff accounts yet</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-stone-200 text-[10px] uppercase tracking-widest text-stone-500">
                  <tr>
                    <th className="px-6 py-4 text-left">Name</th>
                    <th className="px-6 py-4 text-left">Email</th>
                    <th className="px-6 py-4 text-left">Role</th>
                    <th className="px-6 py-4 text-left">Permissions</th>
                    <th className="px-6 py-4 text-left">Status</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {staffList.map((s) => {
                    const roleObj = getRoleObj(s.role);
                    const activePerms =
                      s.permissions && s.permissions.length > 0
                        ? s.permissions
                        : roleObj?.permissions || [];
                    return (
                      <tr key={s.id} className="hover:bg-stone-50">
                        <td className="px-6 py-4 font-medium text-stone-900">
                          {`${s.first_name || ''} ${s.last_name || ''}`.trim() || s.username}
                          <p className="text-xs text-stone-400 font-normal">@{s.username}</p>
                        </td>
                        <td className="px-6 py-4 text-stone-600 text-xs">{s.email}</td>
                        <td className="px-6 py-4">
                          <span
                            className={`text-[10px] font-bold px-2 py-1 rounded border ${roleObj?.color || 'bg-stone-100 text-stone-600 border-stone-200'}`}
                          >
                            {roleObj?.label || s.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-[10px] text-stone-500">
                          {activePerms.length} permissions
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`text-[10px] font-bold px-2 py-1 rounded-full ${s.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                          >
                            {s.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => openEditStaff(s)}
                              className="p-1.5 text-stone-400 hover:text-stone-900 hover:bg-stone-100 rounded transition-colors"
                            >
                              <Edit2 size={14} />
                            </button>
                            {s.status === 'active' && s.role !== 'admin' && (
                              <button
                                onClick={() => handleDeactivate(s.id, s.username)}
                                className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              >
                                <UserX size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          /* ── Roles Table ── */
          <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200 text-[10px] uppercase tracking-widest text-stone-500">
                <tr>
                  <th className="px-6 py-4 text-left">Role</th>
                  <th className="px-6 py-4 text-left">Permissions</th>
                  <th className="px-6 py-4 text-left">Type</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {roles.map((role) => (
                  <tr key={role.id} className="hover:bg-stone-50">
                    <td className="px-6 py-4">
                      <span
                        className={`text-[10px] font-bold px-2 py-1 rounded border ${role.color}`}
                      >
                        {role.label}
                      </span>
                      <p className="text-xs text-stone-400 mt-1">@{role.name}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {(role.permissions || []).slice(0, 4).map((p) => (
                          <span
                            key={p}
                            className="text-[9px] bg-stone-100 text-stone-600 px-1.5 py-0.5 rounded"
                          >
                            {p.replace(/_/g, ' ')}
                          </span>
                        ))}
                        {(role.permissions || []).length > 4 && (
                          <span className="text-[9px] text-stone-400">
                            +{role.permissions.length - 4} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`text-[10px] px-2 py-1 rounded-full ${role.is_system ? 'bg-stone-100 text-stone-500' : 'bg-gold-50 text-gold-700'}`}
                      >
                        {role.is_system ? 'System' : 'Custom'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEditRole(role)}
                          className="p-1.5 text-stone-400 hover:text-stone-900 hover:bg-stone-100 rounded transition-colors"
                        >
                          <Settings size={14} />
                        </button>
                        {!role.is_system && (
                          <button
                            onClick={() => handleDeleteRole(role)}
                            className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Staff Form Modal */}
        {showStaffForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl my-8">
              <div className="flex items-center justify-between px-6 py-5 border-b border-stone-200">
                <h2 className="font-serif text-xl text-stone-900">
                  {editingStaff ? 'Edit Staff Account' : 'Add Staff Account'}
                </h2>
                <button
                  onClick={() => {
                    setShowStaffForm(false);
                    setEditingStaff(null);
                  }}
                  className="text-stone-400 hover:text-stone-900"
                >
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleStaffSubmit} className="p-6 space-y-5">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1">
                      First Name
                    </label>
                    <input
                      value={staffForm.first_name}
                      onChange={(e) => setStaffForm((p) => ({ ...p, first_name: e.target.value }))}
                      className="w-full border border-stone-300 px-3 py-2 text-sm focus:border-gold-600 focus:outline-none rounded-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1">
                      Last Name
                    </label>
                    <input
                      value={staffForm.last_name}
                      onChange={(e) => setStaffForm((p) => ({ ...p, last_name: e.target.value }))}
                      className="w-full border border-stone-300 px-3 py-2 text-sm focus:border-gold-600 focus:outline-none rounded-sm"
                    />
                  </div>
                </div>
                {!editingStaff && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1">
                          Username *
                        </label>
                        <input
                          required
                          value={staffForm.username}
                          onChange={(e) =>
                            setStaffForm((p) => ({ ...p, username: e.target.value }))
                          }
                          className="w-full border border-stone-300 px-3 py-2 text-sm focus:border-gold-600 focus:outline-none rounded-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1">
                          Phone
                        </label>
                        <input
                          value={staffForm.phone}
                          onChange={(e) => setStaffForm((p) => ({ ...p, phone: e.target.value }))}
                          className="w-full border border-stone-300 px-3 py-2 text-sm focus:border-gold-600 focus:outline-none rounded-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1">
                        Email *
                      </label>
                      <input
                        required
                        type="email"
                        value={staffForm.email}
                        onChange={(e) => setStaffForm((p) => ({ ...p, email: e.target.value }))}
                        className="w-full border border-stone-300 px-3 py-2 text-sm focus:border-gold-600 focus:outline-none rounded-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1">
                        Password *
                      </label>
                      <div className="relative">
                        <input
                          required
                          type={showPassword ? 'text' : 'password'}
                          value={staffForm.password}
                          onChange={(e) =>
                            setStaffForm((p) => ({ ...p, password: e.target.value }))
                          }
                          className="w-full border border-stone-300 px-3 py-2 text-sm focus:border-gold-600 focus:outline-none rounded-sm pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((p) => !p)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400"
                        >
                          {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                    </div>
                  </>
                )}
                {/* Role selector from DB */}
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-500 mb-2">
                    Role
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {roles
                      .filter((r) => r.name !== 'admin')
                      .map((role) => (
                        <label
                          key={role.name}
                          className={`flex items-center gap-2 p-3 border rounded-sm cursor-pointer text-xs font-medium transition-all ${staffForm.role === role.name ? `${role.color} border-current` : 'border-stone-200 text-stone-600 hover:border-stone-300'}`}
                        >
                          <input
                            type="radio"
                            name="role"
                            value={role.name}
                            checked={staffForm.role === role.name}
                            onChange={() => handleStaffRoleChange(role.name)}
                            className="sr-only"
                          />
                          {role.label}
                        </label>
                      ))}
                  </div>
                </div>
                {/* Permissions */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs uppercase tracking-widest text-stone-500 font-bold">
                      Custom Permissions
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setStaffForm((p) => ({
                            ...p,
                            permissions: ALL_PERMISSIONS.map((x) => x.key),
                          }))
                        }
                        className="text-[10px] text-gold-600 underline"
                      >
                        All
                      </button>
                      <span className="text-stone-300">|</span>
                      <button
                        type="button"
                        onClick={() => setStaffForm((p) => ({ ...p, permissions: [] }))}
                        className="text-[10px] text-stone-400 underline"
                      >
                        None
                      </button>
                    </div>
                  </div>
                  <div className="border border-stone-200 rounded-sm p-4 space-y-4 max-h-56 overflow-y-auto">
                    {permissionGroups.map((group) => (
                      <div key={group}>
                        <p className="text-[10px] uppercase tracking-widest text-stone-400 font-bold mb-2">
                          {group}
                        </p>
                        <div className="grid grid-cols-2 gap-1.5">
                          {ALL_PERMISSIONS.filter((p) => p.group === group).map((perm) => (
                            <label
                              key={perm.key}
                              className={`flex items-center gap-2 px-3 py-2 rounded-sm cursor-pointer text-xs transition-all ${staffForm.permissions.includes(perm.key) ? 'bg-gold-50 text-gold-800 border border-gold-200' : 'bg-stone-50 text-stone-600 border border-stone-200 hover:border-stone-300'}`}
                            >
                              <input
                                type="checkbox"
                                checked={staffForm.permissions.includes(perm.key)}
                                onChange={() => toggleStaffPerm(perm.key)}
                                className="accent-gold-600 w-3 h-3"
                              />
                              {perm.label}
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-stone-400 mt-1">
                    {staffForm.permissions.length} of {ALL_PERMISSIONS.length} selected
                  </p>
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowStaffForm(false);
                      setEditingStaff(null);
                    }}
                    className="flex-1 border border-stone-300 text-stone-600 py-2.5 text-xs uppercase tracking-widest hover:bg-stone-50 rounded-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingStaff}
                    className="flex-1 bg-stone-900 text-white py-2.5 text-xs uppercase tracking-widest hover:bg-gold-600 rounded-sm disabled:opacity-50"
                  >
                    {savingStaff ? 'Saving...' : editingStaff ? 'Update Staff' : 'Create Account'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Role Form Modal */}
        {showRoleForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl my-8">
              <div className="flex items-center justify-between px-6 py-5 border-b border-stone-200">
                <h2 className="font-serif text-xl text-stone-900">
                  {editingRole ? 'Edit Role' : 'Create Custom Role'}
                </h2>
                <button
                  onClick={() => {
                    setShowRoleForm(false);
                    setEditingRole(null);
                  }}
                  className="text-stone-400 hover:text-stone-900"
                >
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleRoleSubmit} className="p-6 space-y-5">
                <div className="grid grid-cols-2 gap-3">
                  {!editingRole && (
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1">
                        Role ID *
                      </label>
                      <input
                        required
                        value={roleForm.name}
                        onChange={(e) =>
                          setRoleForm((p) => ({
                            ...p,
                            name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
                          }))
                        }
                        placeholder="e.g. marketing"
                        className="w-full border border-stone-300 px-3 py-2 text-sm focus:border-gold-600 focus:outline-none rounded-sm font-mono"
                      />
                      <p className="text-[10px] text-stone-400 mt-1">
                        Lowercase letters, numbers, underscores only
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1">
                      Display Name *
                    </label>
                    <input
                      required
                      value={roleForm.label}
                      onChange={(e) => setRoleForm((p) => ({ ...p, label: e.target.value }))}
                      placeholder="e.g. Marketing"
                      className="w-full border border-stone-300 px-3 py-2 text-sm focus:border-gold-600 focus:outline-none rounded-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-500 mb-2">
                    Badge Color
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    {COLOR_OPTIONS.map((c) => (
                      <label
                        key={c.value}
                        className={`cursor-pointer px-3 py-1.5 rounded border text-xs font-bold ${c.value} ${roleForm.color === c.value ? 'ring-2 ring-stone-900' : ''}`}
                      >
                        <input
                          type="radio"
                          name="color"
                          value={c.value}
                          checked={roleForm.color === c.value}
                          onChange={() => setRoleForm((p) => ({ ...p, color: c.value }))}
                          className="sr-only"
                        />
                        {c.label}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs uppercase tracking-widest text-stone-500 font-bold">
                      Permissions
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setRoleForm((p) => ({
                            ...p,
                            permissions: ALL_PERMISSIONS.map((x) => x.key),
                          }))
                        }
                        className="text-[10px] text-gold-600 underline"
                      >
                        All
                      </button>
                      <span className="text-stone-300">|</span>
                      <button
                        type="button"
                        onClick={() => setRoleForm((p) => ({ ...p, permissions: [] }))}
                        className="text-[10px] text-stone-400 underline"
                      >
                        None
                      </button>
                    </div>
                  </div>
                  <div className="border border-stone-200 rounded-sm p-4 space-y-4 max-h-56 overflow-y-auto">
                    {permissionGroups.map((group) => (
                      <div key={group}>
                        <p className="text-[10px] uppercase tracking-widest text-stone-400 font-bold mb-2">
                          {group}
                        </p>
                        <div className="grid grid-cols-2 gap-1.5">
                          {ALL_PERMISSIONS.filter((p) => p.group === group).map((perm) => (
                            <label
                              key={perm.key}
                              className={`flex items-center gap-2 px-3 py-2 rounded-sm cursor-pointer text-xs transition-all ${roleForm.permissions.includes(perm.key) ? 'bg-gold-50 text-gold-800 border border-gold-200' : 'bg-stone-50 text-stone-600 border border-stone-200 hover:border-stone-300'}`}
                            >
                              <input
                                type="checkbox"
                                checked={roleForm.permissions.includes(perm.key)}
                                onChange={() => toggleRolePerm(perm.key)}
                                className="accent-gold-600 w-3 h-3"
                              />
                              {perm.label}
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-stone-400 mt-1">
                    {roleForm.permissions.length} of {ALL_PERMISSIONS.length} selected
                  </p>
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowRoleForm(false);
                      setEditingRole(null);
                    }}
                    className="flex-1 border border-stone-300 text-stone-600 py-2.5 text-xs uppercase tracking-widest hover:bg-stone-50 rounded-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingRole}
                    className="flex-1 bg-stone-900 text-white py-2.5 text-xs uppercase tracking-widest hover:bg-gold-600 rounded-sm disabled:opacity-50"
                  >
                    {savingRole ? 'Saving...' : editingRole ? 'Update Role' : 'Create Role'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
